import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdminStore } from '../stores/adminStore';
import { Profile } from '../types/profile';
import FilterProfileBar from '../components/common/FilterProfileBar';
import ProfileTable from '../components/profile/ProfileTable';
import { Button } from '../components/common/Button';
import UpdateProfileStatusModal from '../components/UpdateProfileStatusModal';
import AdminProfileDetailsModal from '../components/admin/modals/AdminProfileDetailsModal';
import DeleteUserModal from '../components/admin/modals/DeleteUserModal';
import GenerateAuthLinkModal from '../components/admin/modals/GenerateAuthLinkModal';

const ProfileList: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const {
    profiles,
    profileFilters,
    fetchProfiles,
    fetchMoreProfiles,
    hasMore,
    profilesQueryKey,
    profileListScrollY,
    setProfileListScrollY,
  } = useAdminStore();
  const navigate = useNavigate();

  const location = useLocation();
  const locationState = location.state as { role?: string; selected_id?: string } | null;

  const [filters, setLocalFilters] = useState(() => {
    // When we remount on top of a cached list (e.g. browser Back), the navigation state
    // that originally seeded the filters has already been cleared, so rebuilding from
    // defaults would yield an unfiltered queryKey that no longer matches the cached
    // `profilesQueryKey` — discarding the cache and resetting pagination/scroll. Restore
    // the filters that produced the cached list so the cache-reuse check below matches.
    // An explicit `locationState.role` is a fresh navigation intent and takes precedence.
    if (!locationState?.role && profiles.length > 0 && profilesQueryKey) {
      return { ...profileFilters };
    }
    return {
      role: locationState?.role || '',
      status: '',
      city: '',
      obnl_review_status: '',
      search: '',
    };
  });
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showGenerateLinkModal, setShowGenerateLinkModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const openProfileFromNavigation = async () => {
      if (locationState?.selected_id) {
        const existingProfile = profiles.find(p => p.id === locationState.selected_id);

        if (existingProfile) {
          setSelectedProfile(existingProfile);
          setIsModalOpen(true);
        }
      }
      navigate(location.pathname, { replace: true, state: {} });
    };

    openProfileFromNavigation();
  }, [location.pathname, locationState?.selected_id, navigate, profiles]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  const queryKey = JSON.stringify({
    role: filters.role,
    status: filters.status,
    city: filters.city,
    obnl_review_status: filters.obnl_review_status,
    search: debouncedSearch,
  });

  // Capture, on the very first render, whether we mounted with cached pages for this
  // query. Computed during render (not in an effect) so the scroll-restore layout effect
  // can read it reliably — passive effects run after layout effects, so a ref set inside
  // the load effect below would still be false when the layout effect first runs.
  const reuseCacheOnMountRef = useRef<boolean | null>(null);
  if (reuseCacheOnMountRef.current === null) {
    reuseCacheOnMountRef.current = profiles.length > 0 && profilesQueryKey === queryKey;
  }
  const hasRestoredScrollRef = useRef(false);

  useEffect(() => {
    if (profiles.length > 0 && profilesQueryKey === queryKey) {
      // Cached pages for this query are already in the store; reuse them instead of
      // refetching the first page (which would discard accumulated "Charger plus" pages).
      return;
    }

    const loadProfiles = async () => {
      setLoading(true);
      try {
        await fetchProfiles({
          filters: {
            role: filters.role,
            status: filters.status,
            city: filters.city,
            obnl_review_status: filters.obnl_review_status,
            search: debouncedSearch,
          },
          offsetCount: 0,
          isLoadMore: false,
          queryKey,
        });
      } finally {
        setLoading(false);
      }
    };
    loadProfiles();
  }, [
    debouncedSearch,
    fetchProfiles,
    filters.city,
    filters.obnl_review_status,
    filters.role,
    filters.status,
    queryKey,
    profiles.length,
    profilesQueryKey,
  ]);

  // Restore the scroll position once, only when we mounted with cached profiles.
  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current) return;
    if (!reuseCacheOnMountRef.current) return;

    hasRestoredScrollRef.current = true;
    window.scrollTo(0, profileListScrollY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the scroll position when leaving the page (e.g. navigating to a freelancer profile).
  useEffect(() => {
    return () => {
      setProfileListScrollY(window.scrollY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (name: keyof typeof filters, value: string) => {
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleRowClick = (profile: Profile) => {
    setSelectedProfile(profile);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (profile: Profile) => {
    setSelectedProfile(profile);
    setShowDeleteUserModal(true);
  };

  const handleGenerateLinkClick = (profile: Profile) => {
    setSelectedProfile(profile);
    setShowGenerateLinkModal(true);
  };

  const handleLoadMore = async () => {
    setLoading(true);
    await fetchMoreProfiles();
    setLoading(false);
  };

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <h1 className="text-5xl font-bold hyphens-auto">{t('breadcrumbs.users')}</h1>
      </div>
      <div className="overflow-hidden rounded-2xl bg-white border border-gray-300 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] transition-all">
        <FilterProfileBar filters={filters} onFilterChange={handleFilterChange} />

        <div>
          <ProfileTable
            profiles={profiles}
            onRowClick={handleRowClick}
            onDeleteClick={handleDeleteClick}
            onGenerateLinkClick={handleGenerateLinkClick}
          />
        </div>

        {hasMore?.profiles && (
          <div className="py-4 flex justify-center">
            <Button className="px-8" onClick={handleLoadMore} disabled={loading}>
              {loading
                ? t('loading', { defaultValue: 'Chargement...' })
                : t('loadMore', { defaultValue: 'Charger plus' })}
            </Button>
          </div>
        )}
      </div>

      {isModalOpen && selectedProfile && (
        <AdminProfileDetailsModal
          onClose={() => setIsModalOpen(false)}
          profile={selectedProfile}
          onClick={() => setShowUpdateStatusModal(true)}
        />
      )}

      {showUpdateStatusModal && selectedProfile && (
        <UpdateProfileStatusModal
          user={selectedProfile}
          onClose={() => setShowUpdateStatusModal(false)}
        />
      )}

      {showDeleteUserModal && selectedProfile && (
        <DeleteUserModal profile={selectedProfile} onClose={() => setShowDeleteUserModal(false)} />
      )}

      {showGenerateLinkModal && selectedProfile && (
        <GenerateAuthLinkModal
          profile={selectedProfile}
          onClose={() => setShowGenerateLinkModal(false)}
        />
      )}
    </>
  );
};

export default ProfileList;
